import {
  DocumentDownload,
  DocumentUpload,
  Reload,
  RuggedTrashCan,
} from '@zappyrent/hundop';
import { rem, rgba } from 'polished';
import styled, { css } from 'styled-components';

import {
  IStyledDocumentUploadIconProps,
  IStyledLoadingBarProps,
  IStyledTitleProps,
  IStyledWrapperProps,
} from './DocumentField.types';

export const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background-color: transparent;
  border: none;
  cursor: pointer;
  appearance: none;

  &:not(:first-child) {
    margin-left: ${rem('12px')};
  }
`;

export const StyledDeleteIcon = styled(RuggedTrashCan)`
  width: auto;
  height: ${rem('24px')};

  path {
    fill: ${({ theme }) => theme.colors.cross.red};
  }
`;

export const StyledDocumentDownloadIcon = styled(DocumentDownload)`
  width: ${rem('24px')};
  height: auto;

  path {
    fill: ${({ theme }) => theme.colors.cross.grayson};
  }
`;

export const StyledDocumentUploadIcon = styled(
  DocumentUpload,
)<IStyledDocumentUploadIconProps>`
  width: auto;
  height: ${rem('28px')};

  path {
    transition: all 0.2s;
    fill: ${({ theme, isLoading, user }) =>
      isLoading
        ? theme.colors.cross.silverFox
        : theme.colors[user ?? 'tenant'].main};
  }
`;

export const StyledInnerWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: ${rem('56px')};
  border-radius: ${rem('8px')};
  padding: ${rem('12px')} ${rem('16px')};
  position: relative;
`;

export const StyledInput = styled.input`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
`;

export const StyledLoadingBar = styled.div<IStyledLoadingBarProps>`
  display: block;
  position: absolute;
  width: ${({ loadingProgressPercentage }) =>
    loadingProgressPercentage && loadingProgressPercentage}%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: ${({
    loadingProgressPercentage,
    theme,
    isUploadFailed,
    isUploaded,
    isDeleting,
    user,
  }) => {
    if (isUploadFailed || isDeleting) return rgba(theme.colors.cross.red, 0.1);

    if (loadingProgressPercentage) {
      if (loadingProgressPercentage < 50)
        return rgba(theme.colors.cross.arancin, 0.1);
      if (loadingProgressPercentage === 100 && isUploaded)
        return theme.colors.main;
      else return rgba(theme.colors[user ?? 'tenant'].main, 0.1);
    }
  }};
  transition: all 0.2s;
`;

export const StyledNav = styled.nav`
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  z-index: 2;
`;

export const StyledReloadIcon = styled(Reload)`
  width: ${rem('32px')};
  height: auto;
  border-radius: 50%;
  border: ${rem('1px')} solid ${({ theme }) => theme.colors.cross.silverFox};

  &:hover circle {
    fill: ${({ theme }) => theme.colors.cross.lightGray};
  }

  circle {
    transition: all 0.2s;
  }

  path {
    fill: ${({ theme }) => theme.colors.cross.black};
  }
`;

export const StyledTitle = styled.h5<IStyledTitleProps>`
  margin: 0;
  color: ${({ theme, isUploadFailed, isDeleting }) => {
    if (isUploadFailed || isDeleting) return theme.colors.cross.red;
    else return theme.colors.cross.deepBlu;
  }};
  font-size: ${rem('16px')};
  font-weight: 400;
  position: relative;
  z-index: 2;
  text-align: left;
`;

export const StyledWrapper = styled.div<IStyledWrapperProps>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 0 0 ${rem('8px')} 0;
  border: ${rem('1px')} dashed
    ${({
      theme,
      loadingProgressPercentage,
      isUploadFailed,
      isDeleting,
      user,
    }) => {
      if (isUploadFailed || isDeleting) return theme.colors.cross.red;

      if (loadingProgressPercentage) {
        if (loadingProgressPercentage < 50) return theme.colors.cross.arancin;
        if (loadingProgressPercentage === 100)
          return theme.colors.cross.grayson;
        else return theme.colors[user ?? 'tenant'].main;
      }
    }};
  border-radius: ${rem('8px')};
  transition: all 0.2s;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.main};

  ${({ isUploadFailed, loadingProgressPercentage, theme, user }) =>
    loadingProgressPercentage === null &&
    !isUploadFailed &&
    css`
      @media (hover: hover) {
        &:hover {
          border: ${rem('1px')} dashed ${theme.colors[user ?? 'tenant'].main};
          background-color: ${rgba(
            theme.colors[user ?? 'tenant'].mainPale,
            0.5,
          )};
        }
      }
    `}
`;
